import { CommonModule } from "@angular/common";
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Input,
    OnInit,
    inject
} from "@angular/core";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { MatDialog } from "@angular/material/dialog";
import { MenuOption } from "@app/shared/components/dsn/button-menu-chips/button-menu-chips.component";
import { SharedDsnModule } from "@app/shared/modules/shared-dsn.module";
import { SharedMaterialModule } from "@app/shared/modules/shared-material.module";
import { ContactService } from "@app/shared/services/contact.service";
import { DeviceService } from "@app/shared/services/device.service";
import { PostViewType } from "@app/shared/types/post.view.type";
import { VisualizationPostsBottomSheetComponent } from "@app/views/auth/report/components/visualization-posts-bottom-sheet/visualization-posts-bottom-sheet.component";
import { VisualizationOptionsType } from "@arch/_shared/types/options-visualization";
import { StringUtils } from "@arch/_shared/utils/String.utils";
import { GeneratorIdUtils } from "@arch/_shared/utils/generator-id.utils";
import { EventsService } from "@arch/domain/application/services/events.service";
import { CompanyEntity, PostEntity, ReportEntity } from "@arch/domain/entity";
import { VisualizationEntity } from "@arch/domain/entity/visualization.entity";
import { InsertPostController } from "@arch/presentation/controllers/insert-post.controller";
import { RemovePostController } from "@arch/presentation/controllers/remove-post.controller";
import { ChatMessageComponent } from "../../../../../shared/components/chat-message/chat-message.component";
import { CommentsPostComponent } from "../../../../../shared/components/comments-post/comments-post.component";
import { CommentsPostDialogComponent } from "../report-notes-navigation/comments-post-dialog/comments-post-dialog.component";
import { InteractionsDialogComponent } from "./interactions-dialog/interactions-dialog.component";
@Component({
    selector: "app-visualization-posts",
    imports: [
        CommonModule,
        SharedDsnModule,
        SharedMaterialModule,
        CommentsPostComponent,
        ChatMessageComponent
    ],
    templateUrl: "./visualization-posts.component.html",
    styleUrl: "./visualization-posts.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class VisualizationPostsComponent implements OnInit {
    @Input({ required: true }) company: CompanyEntity;
    @Input({ required: true }) report: ReportEntity;
    @Input({ required: true }) options: VisualizationOptionsType;
    @Input({ required: true }) visualization: VisualizationEntity;
    @Input({ required: false }) previewMode: boolean;
    private readonly deviceService = inject(DeviceService);
    private readonly dialog = inject(MatDialog);
    private readonly bottomSheet = inject(MatBottomSheet);
    private readonly cdk = inject(ChangeDetectorRef);
    private readonly insertPostController = inject(InsertPostController);
    private readonly removePostController = inject(RemovePostController);
    private readonly eventsService = inject(EventsService);
    private readonly contactService = inject(ContactService);

    public contact = this.contactService.get();
    public isMobile = this.deviceService.getIsMobile();
    public postsList: PostViewType[];
    public currentUserLike: PostEntity | null = null;
    public loadingInteractions: boolean = false;
    public likeOptions: MenuOption[] = [
        { label: "Gostei do resultado", value: "liked", selected: false },
        { label: "Gostei do indicador", value: "liked", selected: false },
        { label: "Gostei da análise", value: "liked", selected: false },
        { label: "Gostei, mas tenho dúvidas", value: "liked", selected: false }
    ];
    public unlikeOptions: MenuOption[] = [
        {
            label: "Não gostei do resultado",
            value: "disliked",
            selected: false
        },
        {
            label: "Não gostei do indicador",
            value: "disliked",
            selected: false
        },
        { label: "Não gostei da análise", value: "disliked", selected: false },
        { label: "Tenho dúvidas", value: "doubts", selected: false }
    ];

    ngOnInit(): void {
        this.modelerPosts();
        this.checkCurrentUserLike();
        this.updateOptionsState();
    }

    public modelerPosts(): void {
        const latestTwoPosts = this.getLatestTwoPosts();
        this.postsList = latestTwoPosts.map((post) => ({
            _id: post.props._id,
            initials: StringUtils.getInitials(post.props.u.n),
            message: post.props.comment.msg,
            date: post.commentDate.toLocaleDateString("pt-BR", {
                day: "numeric",
                month: "long"
            }),
            userName: post.props.u.n,
            userId: post.props.u._id
        }));
    }

    public getLatestTwoPosts(): PostEntity[] {
        return this.visualization.getPostsSortedByDate().slice(0, 2).reverse();
    }

    private checkCurrentUserLike(): void {
        if (
            this.visualization.postsLike &&
            this.visualization.postsLike.length > 0
        ) {
            this.currentUserLike =
                this.visualization.postsLike.find(
                    (like) => like.props.u._id === this.contact.props._id
                ) || null;
        } else {
            this.currentUserLike = null;
        }
    }

    public hasUserLiked(): boolean {
        return this.currentUserLike !== null;
    }

    public getUserLikeOption(): string {
        if (this.currentUserLike && this.currentUserLike.props.comment) {
            return this.currentUserLike.props.comment.msg || "";
        }
        return "";
    }

    public getUserLikeType(): string {
        if (this.currentUserLike && this.currentUserLike.props.comment) {
            return this.currentUserLike.props.comment.optionLike || "";
        }
        return "";
    }

    public getSelectedLikeOption(): MenuOption | null {
        if (!this.hasUserLiked()) return null;

        const userLikeOption = this.getUserLikeOption();
        const allOptions = [...this.likeOptions, ...this.unlikeOptions];
        return (
            allOptions.find((option) => option.label === userLikeOption) || null
        );
    }

    private updateOptionsState(): void {
        const userLikeOption = this.getUserLikeOption();

        // Marca as opções de like baseado no comment.msg
        this.likeOptions = this.likeOptions.map((option) => ({
            ...option,
            selected: option.label === userLikeOption
        }));

        // Marca as opções de dislike baseado no comment.msg
        this.unlikeOptions = this.unlikeOptions.map((option) => ({
            ...option,
            selected: option.label === userLikeOption
        }));
    }

    public onOpenPosts(): void {
        if (this.previewMode) {
            this.openPostsDialog();
        } else {
            this.openPostsBottomSheet();
        }
    }

    private openPostsDialog(): void {
        this.dialog.open(CommentsPostDialogComponent, {
            width: "100%",
            maxWidth: "524px",
            height: "100%",
            maxHeight: "514px",
            data: {
                contact: this.contact,
                report: this.report,
                visualization: this.visualization,
                company: this.company,
                options: this.options
            }
        });
    }

    private openPostsBottomSheet(): void {
        const bottomSheetRef = this.bottomSheet.open(
            VisualizationPostsBottomSheetComponent,
            {
                data: {
                    company: this.company,
                    options: this.options,
                    visualization: this.visualization,
                    contact: this.contact,
                    report: this.report
                }
            }
        );
        bottomSheetRef.afterDismissed().subscribe(() => {
            this.modelerPosts();
            this.cdk.detectChanges();
        });
    }

    public async onSelectLikeOption(
        option: MenuOption,
        likeType?: "liked" | "disliked"
    ): Promise<void> {
        this.eventsService.send(
            `Dash.${this.report.type}.${this.report.humanSourceName}.interagir`
        );
        try {
            // Se o usuário já deu like, remove o like anterior

            if (this.currentUserLike) {
                if (
                    ((likeType == "liked" &&
                        this.getUserLikeType() === "liked") ||
                        (likeType == "disliked" &&
                            this.getUserLikeType() === "disliked")) &&
                    this.currentUserLike.props.comment?.msg === option.label
                ) {
                    this.removePostController.execute({
                        postId: this.currentUserLike.props._id,
                        userId: this.currentUserLike.props.u._id
                    });

                    if (this.visualization.postsLike) {
                        this.visualization.postsLike =
                            this.visualization.postsLike.filter(
                                (like) =>
                                    like.props.u._id !== this.contact.props._id
                            );
                    }

                    this.checkCurrentUserLike();
                    this.updateOptionsState();

                    return;
                }

                this.removePostController.execute({
                    postId: this.currentUserLike.props._id,
                    userId: this.currentUserLike.props.u._id
                });

                // Remove o like da lista local
                if (this.visualization.postsLike) {
                    this.visualization.postsLike =
                        this.visualization.postsLike.filter(
                            (like) =>
                                like.props.u._id !== this.contact.props._id
                        );
                }
            }

            if (option.label !== this.currentUserLike?.props?.comment?.msg) {
                // Insere o novo like
                const postId = GeneratorIdUtils.execute();
                this.insertPostController.execute({
                    _id: postId,
                    accountingId: this.company.props.accountingId,
                    companyId: this.company.props._id,
                    comment: {
                        msg: option.label,
                        t: new Date(),
                        optionLike: option.value
                    },
                    scenarioId: this.options.scenario.props._id,
                    periodDate: this.options.endDate,
                    user: {
                        _id: this.contact.props._id,
                        n: this.contact.props.name,
                        isContact: this.contact.isContact
                    },
                    visualizationId: this.visualization.props._id,
                    reportId: this.report.props._id,
                    reportName: this.report.props.name,
                    indicatorName: this.visualization.props.name,
                    companyName: this.company.props.name,
                    isLike: true,
                    toUserId: this.report.props.userId,
                    isGroup: this.company.isGroup
                });

                // Cria o post entity para o like
                const likePost = PostEntity.restore({
                    _id: postId,
                    comment: {
                        msg: option.label,
                        t: new Date().toISOString(),
                        optionLike: option.value
                    },
                    u: {
                        _id: this.contact.props._id,
                        n: this.contact.props.name
                    },
                    ac: this.company.props.accountingId,
                    c: this.company.props._id,
                    v: this.visualization.props._id,
                    t: new Date().toISOString(),
                    group: [],
                    isLike: true
                });

                // Atualiza os likes da visualização
                const updatedLikes = this.visualization.postsLike || [];
                updatedLikes.push(likePost);

                // Passa todos os posts (normais + likes) para setPosts fazer a separação correta
                const allPosts = [...this.visualization.posts, ...updatedLikes];
                this.visualization.setPosts(allPosts);
            }

            // Verifica novamente o like do usuário atual e atualiza o estado
            if (this.currentUserLike) {
                this.checkCurrentUserLike();
            } else {
                this.checkCurrentUserLike();
            }

            this.updateOptionsState();

            // Recarrega os posts na tela
            this.modelerPosts();
            this.cdk.detectChanges();
        } catch (error) {
            console.error("Erro ao processar like:", error);
        }
    }

    public async onUpdateLike(): Promise<void> {
        if (this.currentUserLike) {
            await this.removePostController.execute({
                postId: this.currentUserLike.props._id,
                userId: this.currentUserLike.props.u._id
            });

            this.checkCurrentUserLike();
        }
    }

    public onOpenInteractionsDialog(): void {
        this.dialog.open(InteractionsDialogComponent, {
            width: "100%",
            maxWidth: "502px",
            height: "100%",
            maxHeight: "246px",
            data: {
                contact: this.contact,
                visualization: this.visualization
            }
        });
    }
}
